import {groupBy} from "./utils.ts";
import { nanoid } from 'nanoid'
import {
    DataSyncManagerBase,
    LOCAL_STORE_ADMIN_KEY,
    LOCAL_STORE_KEY_PREFIX,
    LOCAL_STORE_META_KEY
} from "./DataSyncManagerBase.ts";

// Re-export constants for use in tests
export { LOCAL_STORE_KEY_PREFIX, LOCAL_STORE_META_KEY };

export class DataSyncManager extends DataSyncManagerBase
{
    private userId: string = ''  //not actually used for now
    private userName: string = ''
    private token: string = ''
    private readonly recordMap: {[id: string]: DataRecord} = {}
    private currentAction: Promise<boolean>|null = null;
    private readonly updateQueue: string[] = []
    private loadAllEnqueued: boolean = false

    public onRecordLoaded: ((id: string, data: DataItem) => void)|null = null
    public onRecordDeleted: ((id: string) => void)|null = null
    public onConflict = (_id: string, respond: (keepLocalChanges: boolean) => void): void => {
        const keepLocalChanges = confirm("Conflicting changes on the server (parallel editing on another device). Keep local changes?")
        respond(keepLocalChanges)
    }
    public onSyncFinished: (() => void)|null = null

    constructor(apiPrefix: string = '', localStorage_: Storage|null = null) {
        super(apiPrefix, localStorage_)
    }

    public getAllRecords(): {[id: string]: DataItem} {
        const result: {[id: string]: DataItem} = {}
        for (let id in this.recordMap) {
            if (this.recordMap[id].data != null) {
                result[id] = this.recordMap[id].data
            }
        }
        return result
    }

    public isLoggedIn() {
        return !!(this.userId && this.token)
    }

    public getUserName() {
        return this.userName
    }

    public getToken() {
        return this.token
    }

    private askConflict(id: string): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.onConflict(id, (keepLocalChanges) => resolve(keepLocalChanges))
        })
    }

    private async handleConflict(id: string, item: DataRecord, serverVersion: number): Promise<boolean> {
        const keepLocalChanges = await this.askConflict(id)

        if (keepLocalChanges) {
            item.serverVersion = serverVersion
            this.enqueueUpdate(id, item)
            return true
        } else {
            this.removeFromUpdateQueue(id)
            if (item.serverKey !== '') {
                item.syncedLocalVersion = item.localVersion
                return await this.loadRecord(id, item.serverKey, -1)
            }
            else {
                delete this.recordMap[id]
                this.deleteInLocalStorage(id)
                this.onRecordDeleted && this.onRecordDeleted(id)
                return true
            }
        }
    }

    private saveToLocalStorage(id: string, item: DataRecord) {
        this.localStorage.setItem(LOCAL_STORE_KEY_PREFIX + id, JSON.stringify(item))
    }

    private deleteInLocalStorage(id: string) {
        this.localStorage.removeItem(LOCAL_STORE_KEY_PREFIX + id)
    }

    public initialLoad(): Promise<boolean>
    {
        const promise = this.doInitialLoad()
        return this.setCurrentAction(promise)
    }

    private async doInitialLoad(): Promise<boolean>
    {
        const {userId, userName, token} = JSON.parse(this.localStorage.getItem(LOCAL_STORE_META_KEY) || '{}')
        this.userId = userId
        this.userName = userName
        this.token = token

        for (let i=0; i<this.localStorage.length; i++) {
            const key = this.localStorage.key(i);
            if (key?.startsWith(LOCAL_STORE_KEY_PREFIX) && key != LOCAL_STORE_META_KEY && key != LOCAL_STORE_ADMIN_KEY) {
                const item = JSON.parse(this.localStorage.getItem(key) || 'null')
                if (item) {
                    const id = key.substring(LOCAL_STORE_KEY_PREFIX.length)
                    this.recordMap[id] = item
                }
            }
        }

        if (!this.isLoggedIn())  return true

        return this.loadDataFromServer()
    }

    private async loadDataFromServer(): Promise<boolean>
    {
        let response = await fetch(this.apiPrefix+'/data', {
            method: 'GET',
            headers: { 'X-Auth-Token': this.token }
        } as RequestInit)
        if (!response.ok) {
            this.showError('Failed to load data from server', 'Error response from server');
            return false
        }
        let result = await response.json();
        if (!result || result.success !== true || !Array.isArray(result.records) || result.records.some((it: any) => !it.key || !Number.isInteger(it.version))) {
            this.showError('Failed to load data from server' + (result?.error ? ': ' + result.error : ''), 'Invalid response from server: ' + (result?.error || JSON.stringify(result)));
            return false
        }
        const records = result.records as {key: string, version: number, public_key: string}[]
        const serverKeyToLocalIdMap = groupBy(Object.entries(this.recordMap).filter(it => it[1].serverKey), it => it[1].serverKey, it => it[0])
        const removedLocalRecords = {...this.recordMap}

        // load changed records from server
        for (let serverItem of records) {
            const localId = serverKeyToLocalIdMap[serverItem.key]
            const localItem = localId ? this.recordMap[localId] : null
            if (localItem)  delete removedLocalRecords[localId]
            if (!localItem || localItem.serverVersion != serverItem.version) {
                if (localItem && localItem.localVersion > localItem.syncedLocalVersion) {
                    await this.handleConflict(localId, localItem, serverItem.version)
                } else {
                    await this.loadRecord(localId, serverItem.key, -1)
                }
            }
            if (localItem) {
                localItem.publicKey = serverItem.public_key || ''
            }
        }
        // delete orphan local records (that were deleted on server)
        for (let id in removedLocalRecords) {
            let item = this.recordMap[id]
            if (item.serverKey != '') {  //item.serverKey == '' means the record was created on our side and is not synced yet
                if (item.localVersion > item.syncedLocalVersion) {
                    item.serverKey = ''
                    await this.handleConflict(id, item, -1)
                } else {
                    delete this.recordMap[id]
                    this.deleteInLocalStorage(id)
                    this.onRecordDeleted && this.onRecordDeleted(id)
                }
            }
        }
        // enqueue local changes (that were not synchronized last time)
        for (let id in this.recordMap) {
            let item = this.recordMap[id]
            if (item.localVersion > item.syncedLocalVersion) {
                this.enqueueUpdate(id, item)
            }
        }

        return true
    }

    private async loadRecord(id: string|null, key: string, version: number): Promise<boolean>
    {
        const res = await this.fetchRecord(key, version)
        if (res == null)  return false
        return await this.applyLoadedRecord(id, key, res.version, res.data, res.publicKey)
    }

    private async fetchRecord(key: string, version: number): Promise<{ version: number, data: DataItem|null, publicKey: string } | null>
    {
        const response = await fetch(this.apiPrefix+'/data/' + key + "?version=" + version, {
            method: 'GET',
            headers: { 'X-Auth-Token': this.token }
        } as RequestInit)
        if (response.status == 404) {
            return { version: -1, data: null, publicKey: ''}
        }
        if (!response.ok) {
            this.showError('Failed to load data from server', 'Error response from server');
            return null
        }
        const serverVersion = parseInt(response.headers.get("X-Data-Version") as string)
        if (isNaN(serverVersion)) {
            this.showError('Failed to load data from server', 'Wrong X-Data-Version header in server response');
            return null
        }
        const data = await response.json()
        if (!data) {
            this.showError('Failed to load data from server', 'Wrong JSON response from server');
            return null
        }
        if (!data.id) {
            this.showError('Failed to load data from server', 'No ID in data item');
            return null
        }
        const publicKey = response.headers.get("X-Data-Public-Key") as string || ''
        return { version: serverVersion, data, publicKey }
    }

    private async applyLoadedRecord(id: string|null, serverKey: string, serverVersion: number, data: DataItem|null, publicKey: string): Promise<boolean>
    {
        if (data == null)  //record has beed deleted on server
        {
            let item = id && this.recordMap[id]
            if (item) {
                if (item.localVersion > item.syncedLocalVersion) {
                    const keepLocalChanges = await this.askConflict(id!)
                    if (keepLocalChanges) {
                        item.serverKey = ''
                        item.serverVersion = -1
                        this.enqueueUpdate(id!, item)
                        return true
                    } else {
                        this.removeFromUpdateQueue(id!)
                    }
                }
                delete this.recordMap[id!]
                this.deleteInLocalStorage(id!)
                this.onRecordDeleted && this.onRecordDeleted(id!)
            }
            else {
                //new record already deleted (should be rare case but possible)
            }
            return true
        }

        if (id == null)  id = data!.id

        let item = this.recordMap[id]
        if (!item) {
            item = {
                localVersion: 1,
                syncedLocalVersion: 1,
                serverKey: serverKey,
                serverVersion: serverVersion,
                data: data,
                publicKey: publicKey
            }
            this.recordMap[id] = item
        } else {
            if (item.localVersion > item.syncedLocalVersion) {
                const keepLocalChanges = await this.askConflict(id)
                if (keepLocalChanges) {
                    item.serverVersion = serverVersion
                    this.enqueueUpdate(id, item)
                    return true
                }
                item.syncedLocalVersion = item.localVersion
                this.removeFromUpdateQueue(id)
            }
            item.serverVersion = serverVersion
            item.data = data
            item.publicKey = publicKey
        }
        this.saveToLocalStorage(id, item)
        this.onRecordLoaded && this.onRecordLoaded(id, data)

        return true
    }

    public async signUp(login: string, password: string): Promise<boolean>
    {
        const res = await this.doCreateUser(login, password)
        if (res) {
            // enqueue all local records
            for (let [id, record] of Object.entries(this.recordMap)) {
                this.enqueueUpdate(id, record)
            }
        }
        return res
    }

    private async doCreateUser(login: string, password: string): Promise<boolean>
    {
        if (this.isLoggedIn())  throw new Error("Already logged in, please logout first")

        let response = await fetch(this.apiPrefix+'/user/new', {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ login, password })
        } as RequestInit);
        let result = await this.checkSuccessResponse(response, [409], 'Failed to create a new user', (res) => res.user_id && res.token)
        if (!result)  return false

        this.userId = result.user_id
        this.userName = login
        this.token = result.token
        this.localStorage.setItem(LOCAL_STORE_META_KEY, JSON.stringify({userId: this.userId, userName: login, token: this.token}))

        return true
    }

    public async login(login: string, password: string, awaitFullSync: boolean = true): Promise<boolean>
    {
        if (this.isLoggedIn())  throw new Error("Already logged in, please logout first")

        let response = await fetch(this.apiPrefix+'/login', {
            method: 'POST',
            headers: {},
            body: JSON.stringify({ login, password })
        })
        let result = await this.checkSuccessResponse(response, [401], 'Failed to login', (res) => res.user_id && res.token)
        if (!result)  return false

        this.userId = result.user_id
        this.userName = login
        this.token = result.token
        this.localStorage.setItem(LOCAL_STORE_META_KEY, JSON.stringify({userId: this.userId, userName: login, token: this.token}))

        this.setCurrentAction(this.loadDataFromServer())
        return awaitFullSync ? this.currentAction! : true
    }

    public async logout(): Promise<boolean>
    {
        if (!this.isLoggedIn())  throw new Error("Not logged in")

        const success = await this.executeLogout()

        const that = this
        const promise = new Promise<boolean>(resolve => {
            that.userId = ''
            that.token = ''
            that.localStorage.setItem(LOCAL_STORE_META_KEY, JSON.stringify({userId: '', userName: that.userName, token: ''}))
            resolve(success)
        })
        return this.setCurrentAction(promise)
    }

    public async exit(): Promise<boolean>
    {
        if (!this.isLoggedIn())  throw new Error("Not logged in")

        const success = await this.executeLogout()

        const that = this
        const promise = new Promise<boolean>(resolve => {
            that.userId = ''
            that.userName = ''
            that.token = ''
            for (let id of Object.keys(that.recordMap)) {
                that.deleteInLocalStorage(id)
                delete that.recordMap[id]
            }
            that.localStorage.removeItem(LOCAL_STORE_META_KEY)
            resolve(success)
        })
        return this.setCurrentAction(promise)
    }

    private async executeLogout(): Promise<boolean>
    {
        let response = await fetch(this.apiPrefix+'/logout', {
            method: 'POST',
            headers: { 'X-Auth-Token': this.token },
        })
        let result = await this.checkSuccessResponse(response, [], 'Failed to logout')  //todo token may remain on the server in case of an error, it may be better to delete it later somehow
        return result != null
    }

    public async changePassword(oldPassword: string, newPassword: string): Promise<boolean>
    {
        if (!this.isLoggedIn())  throw new Error("Not logged in")

        let response = await fetch(this.apiPrefix+'/user/password', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Auth-Token': this.token },
            body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
        })
        if (!await this.checkSuccessResponse(response, [400, 401], 'Failed to change password')) {
            return false
        }

        return true
    }

    private getRecord(id: string): DataRecord {
        let item = this.recordMap[id]
        if (!item) {
            throw new Error("Data record is not found: " + id)
        }
        return item
    }

    public getCurrentAction(): Promise<boolean>|null {
        return this.currentAction
    }

    private setCurrentAction(promise: Promise<boolean>): Promise<boolean> {
        const that = this
        promise = promise.finally(async () => {
            // apply all pending updates
            let res = true
            while (that.updateQueue.length > 0) {
                const nextId = that.updateQueue.shift()!
                if (!await that.applyRecordUpdate(nextId, that.getRecord(nextId)))  res = false
            }
            if (that.loadAllEnqueued) {
                if (!await that.loadDataFromServer())  res = false
            }
            that.loadAllEnqueued = false
            that.currentAction = null
            that.actionChainFinished()
            return res
        })
        that.currentAction = that.currentAction != null ? that.currentAction.finally(() => promise) : promise
        return that.currentAction
    }

    private enqueueUpdate(id: string, item: DataRecord) {
        if (this.currentAction != null) {
            if (this.updateQueue.includes(id)) {
                // already has an update request for this id, don't need to make a new one
            } else {
                this.updateQueue.push(id)
            }
            return
        }

        this.setCurrentAction(this.applyRecordUpdate(id, item))
    }

    private applyRecordUpdate(id: string, item: DataRecord): Promise<boolean> {
        if (item.localVersion === item.syncedLocalVersion) {
            // record was updated but somehow remained in the queue - should not normally happen, but do nothing in this case
            console.debug("Enqueued record was already updated: " + id)
            return Promise.resolve(true)
        } else if (item.serverKey === '' && item.data === null) {
            // record was deleted before saved to server - do nothing
            return Promise.resolve(true)
        } else if (item.serverKey === '') {
            return this.doCreateNewRecord(id, item)
        } else if (item.data === null) {
            return this.doDeleteRecord(id, item)
        } else {
            return this.doUpdateRecord(id, item)
        }
    }

    private removeFromUpdateQueue(id: string) {
        const i = this.updateQueue.indexOf(id)
        if (i >= 0)  this.updateQueue.splice(i, 1)
    }

    private actionChainFinished() {
        this.onSyncFinished && this.onSyncFinished()
    }

    public createNewRecord(data: DataItem): string
    {
        if (!data.id) {
            data.id = nanoid()
        }
        let id = data.id
        let item: DataRecord = {
            localVersion: 1,
            syncedLocalVersion: 0,
            serverKey: '',
            serverVersion: -1,
            data: data,
            publicKey: ''
        }
        this.recordMap[id] = item
        this.saveToLocalStorage(id, item)

        if (this.isLoggedIn())  this.enqueueUpdate(id, item)

        return id
    }

    private async doCreateNewRecord(id: string, item: DataRecord): Promise<boolean>
    {
        let syncingVersion = item.localVersion
        let response = await fetch(this.apiPrefix+'/data/new', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Auth-Token': this.token },
            body: JSON.stringify(item.data)
        } as RequestInit);
        let result = await this.checkSuccessResponse(response, [], 'Failed to sync new data on server', (res) => res.key && typeof res.version === 'number')
        if (!result)  return false

        item.syncedLocalVersion = syncingVersion
        item.serverKey = result.key
        item.serverVersion = result.version
        this.saveToLocalStorage(id, item)

        return true
    }

    public updateRecord(data: DataItem)
    {
        let id = data.id
        if (!id) {
            this.showError("Unexpected error", "No ID item: " + JSON.stringify(data))
            return
        }

        let item = this.getRecord(id)
        if (item.data == null) {
            this.showError("Unexpected error", "Cannot update a deleted data record: " + id)
            return
        }

        item.localVersion++
        item.data = data
        this.saveToLocalStorage(id, item)

        if (this.isLoggedIn())  this.enqueueUpdate(id, item)
    }

    private async doUpdateRecord(id: string, item: DataRecord): Promise<boolean> {
        let syncingVersion = item.localVersion
        let response = await fetch(this.apiPrefix+'/data/' + item.serverKey + "?version=" + item.serverVersion, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Auth-Token': this.token },
            body: JSON.stringify(item.data)
        } as RequestInit);
        if (response.status == 409) {
            const result = await response.json();
            if (!result || !Number.isInteger(result.version)) {
                this.showError('Failed to sync data on server', 'Wrong `version` in server response');
                return false
            }
            this.loadAllEnqueued = true
            return await this.handleConflict(id, item, result.version)
        }
        else if (response.status == 404) {
            item.serverKey = ''
            this.loadAllEnqueued = true
            return await this.handleConflict(id, item, -1)
        }
        let result = await this.checkSuccessResponse(response, [], 'Failed to sync data on server', (res) => Number.isInteger(res.version))
        if (!result)  return false

        item.syncedLocalVersion = syncingVersion
        item.serverVersion = result.version
        this.saveToLocalStorage(id, item)

        return true
    }

    public deleteRecord(id: string)
    {
        let item = this.getRecord(id)

        item.localVersion++
        item.data = null
        this.saveToLocalStorage(id, item)

        if (this.isLoggedIn())  this.enqueueUpdate(id, item)
    }

    private async doDeleteRecord(id: string, item: DataRecord): Promise<boolean> {
        let response = await fetch(this.apiPrefix+'/data/' + item.serverKey + "?version=" + item.serverVersion, {
            method: 'DELETE',
            headers: { 'X-Auth-Token': this.token }
        } as RequestInit);
        if (response.status == 409) {
            const result = await response.json();
            if (!result || !Number.isInteger(result.version)) {
                this.showError('Failed to sync data on server', 'Wrong `version` in server response');
                return false
            }
            this.loadAllEnqueued = true
            return await this.handleConflict(id, item, result.version)
        }
        else if (response.status == 404) {
            console.warn("Record was already deleted on the server: " + JSON.stringify(item))
            //treat it as successful deletion
        }
        else if (!await this.checkSuccessResponse(response, [], 'Failed to sync removed data on server')) {
            return false
        }

        delete this.recordMap[id]
        this.deleteInLocalStorage(id)

        return true
    }

    public publishData(id: string): Promise<boolean>
    {
        let item = this.getRecord(id)
        return this.setCurrentAction(this.doPublishData(item))
    }

    private async doPublishData(item: DataRecord): Promise<boolean>
    {
        const response = await fetch(this.apiPrefix+'/data/' + item.serverKey + "/share", {
            method: 'POST',
            headers: { 'X-Auth-Token': this.token }
        } as RequestInit);
        const result = await this.checkSuccessResponse(response, [], 'Failed to publish data', (res) => res.public_key)
        if (!result) {
            return false
        }
        item.publicKey = result.public_key
        return true
    }

    public unpublishData(id: string): Promise<boolean>
    {
        let item = this.getRecord(id)
        return this.setCurrentAction(this.doUnpublishData(item))
    }

    private async doUnpublishData(item: DataRecord): Promise<boolean>
    {
        const response = await fetch(this.apiPrefix+'/data/' + item.serverKey + "/share", {
            method: 'DELETE',
            headers: { 'X-Auth-Token': this.token }
        } as RequestInit);
        const result = await this.checkSuccessResponse(response, [], 'Failed to unpublish data')
        if (!result) {
            return false
        }
        item.publicKey = ''
        return true
    }

    public getPublicKey(id: string): string|null {
        return this.getRecord(id).publicKey || null
    }

    public async loadPublicData(publicKey: string): Promise<DataItem|null> {
        const response = await fetch(this.apiPrefix + '/data/shared/' + publicKey, {
            method: 'GET',
            headers: {}
        } as RequestInit)
        if (response.status == 404) {
            this.showWarning('Not found');
            return null
        }
        if (!response.ok) {
            this.showError('Failed to load public data', 'Error response from server');
            return null
        }
        const data = await response.json()
        if (!data || !data.id) {
            this.showError('Failed to load public data', 'Invalid response from server');
            return null
        }
        return data
    }
}

interface DataRecord
{
    localVersion: number
    syncedLocalVersion: number
    serverKey: string
    serverVersion: number
    data: DataItem|null
    publicKey: string
}

// this can be removed if we switch to user-generated IDs, i.e. keeping id on the server will not be unnecessary
export interface DataItem
{
    id: string
}